import { formatBytes, type RecordFileItem } from '@ofmos/shared/files';
import type { RefObject } from 'react';
import { BottomSheet, Button } from '../../ui';
import { FilePreview } from './FilePreview';
import { isPreviewable } from './fileItems';
import { useFileDownload } from './useFileDownload';

/**
 * THE PHONE'S FILE SHEET (`docs/decisions/file-attachments.md` D18), OWNED BY THE FIELD.
 *
 * D18: the row is one tap target and this is what it opens - the name, the size, the type,
 * the image preview, and two FULL-WIDTH actions a thumb can reach. The destructive
 * confirmation lives IN HERE rather than in a row-level popover, "because a popover anchored
 * to a 32-pixel row on a phone is a mis-tap generator".
 *
 * WHAT WAVE 1c CHANGED IS THE OWNER, NOT THE ARRANGEMENT. `FileSheetRow` used to hold this
 * sheet, and a sheet held by the row it removes UNMOUNTS on the removal instead of closing -
 * so Radix's `onCloseAutoFocus` never fires, the focus scope is torn down mid-trap, and focus
 * is left on `<body>` (measured: polled every 120ms for 1.2s, wave 1b). The field mounts one
 * sheet for the whole list now; removing a file closes it, and a close returns focus.
 *
 * IT KEEPS RENDERING THE FILE IT WAS OPENED WITH. `item` is the LAST selection rather than
 * the current one, because Radix keeps the content mounted through the exit animation: the
 * caller hands the same item down while the sheet is closing, so the removal does not blank
 * out the title of the sheet that is announcing it.
 */
export interface FileSheetProps {
  readonly item: RecordFileItem;
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  /** Absent means read-only: an archived record, a timeline value, a diff. */
  readonly onRemove?: (() => void) | undefined;
  /** Whether the field is currently asking about THIS file. Field state (wave 1c). */
  readonly confirming: boolean;
  readonly onAskRemove: () => void;
  readonly onCancelRemove: () => void;
  /** Where focus goes when the row that opened this sheet did not survive the removal. */
  readonly returnFocusTo?: RefObject<HTMLElement | null> | undefined;
  /** The write that links this file has not settled, so its preview waits (`FilePreview`). */
  readonly pending?: boolean | undefined;
}

export function FileSheet({
  item,
  open,
  onOpenChange,
  onRemove,
  confirming,
  onAskRemove,
  onCancelRemove,
  returnFocusTo,
  pending = false,
}: FileSheetProps) {
  const { busy, download } = useFileDownload(item);

  return (
    <BottomSheet
      open={open}
      onOpenChange={onOpenChange}
      title={item.label}
      description={`${formatBytes(item.byteCount)} - ${item.contentType}`}
      {...(returnFocusTo !== undefined ? { returnFocusTo } : {})}
    >
      <div className="space-y-3">
        {/* Bounded from above and centred, never upscaled - see `FileRow`'s dialog. */}
        {isPreviewable(item) && (
          <FilePreview item={item} pending={pending} className="mx-auto max-h-[45dvh] max-w-full" />
        )}

        {confirming ? (
          /**
           * The confirmation REPLACES the actions rather than stacking under them: a sheet
           * that grows a second set of buttons while the first is still tappable is how a
           * destructive action gets hit by the thumb that was aiming at Download.
           */
          <div className="space-y-2">
            <p className="text-sm text-ink">
              Remove {item.label}? The file is deleted, and this cannot be undone.
            </p>
            <Button variant="danger" block onClick={() => onRemove?.()}>
              Remove
            </Button>
            <Button variant="ghost" block onClick={onCancelRemove}>
              Cancel
            </Button>
          </div>
        ) : (
          <div className="space-y-2">
            <Button variant="secondary" block icon="arrow-down" loading={busy} onClick={download}>
              Download
            </Button>
            {onRemove !== undefined && (
              <Button variant="danger" block icon="trash" onClick={onAskRemove}>
                Remove
              </Button>
            )}
          </div>
        )}
      </div>
    </BottomSheet>
  );
}
